import React, { useState, useRef, useEffect, useCallback } from 'react';
import colors from '../../utils/colors';
import typography from '../../utils/typography';
import {
  MusicNoteSlashIcon,
  InfoIcon,
  ChevronDownIcon
} from '../../utils/icons';

// --- Drag Tooltip Component ---
const DragTooltip = ({ value, isVisible, parentHandleRef }) => {
  if (!isVisible || !parentHandleRef.current) return null;
  let text = "";
  if (value <= 33) text = "Safe zone";
  else if (value <= 66) text = "Normal results";
  else if (value <= 81) text = "Experimental results";
  else text = "Chaos mode";
  
  const tooltipTopOffset = -40; 
  const handleLeft = parentHandleRef.current.offsetLeft; 
  const handleWidth = parentHandleRef.current.offsetWidth; 
  const tooltipHorizontalAnchorPoint = handleLeft + (handleWidth / 2);

  return (
    <div
      className="absolute flex justify-center items-center z-20 text-xs font-medium text-center whitespace-nowrap select-none pointer-events-none"
      style={{
        padding: '8px 12px',
        borderRadius: '8px',
        left: `${tooltipHorizontalAnchorPoint}px`,
        top: `${tooltipTopOffset}px`,
        transform: 'translateX(-50%)', 
        backdropFilter: 'blur(25px)', 
        WebkitBackdropFilter: 'blur(25px)', 
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        color: colors.text.primary
      }}
    >{text}</div>
  );
};

// --- Info Tooltip Component ---
const InfoTooltip = ({ text, isVisible }) => {
  if (!isVisible || !text) return null;
  return (
    <div
      className="absolute flex justify-center items-center z-30 text-xs text-center select-none pointer-events-none"
      style={{
        padding: '8px 12px',
        left: '0px',
        bottom: 'calc(100% + -8px)', 
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(25px)', 
        WebkitBackdropFilter: 'blur(25px)',
        borderRadius: '8px',
        color: colors.text.primary,
        fontWeight: 500
      }}
    >{text}</div>
  );
};

// --- Slider Track Component ---
const SliderTrack = ({ currentValue = 50 }) => {
  const peakHighlightPattern = [0.05, 0.1, 0.15, 0.25, 0.4, 0.25, 0.15, 0.1, 0.05];
  const numPatternBars = peakHighlightPattern.length;
  const patternCenterIndex = Math.floor(numPatternBars / 2);
  const numScreenBarsToRender = 11; 
  const handleAlignedScreenBarIndex = Math.round((currentValue / 100) * (numScreenBarsToRender - 1));
  const displayedOpacities = Array(numScreenBarsToRender).fill(0).map((_, screenBarIndexOnDisplay) => {
    const diffFromHandle = screenBarIndexOnDisplay - handleAlignedScreenBarIndex;
    const patternIndexToUse = patternCenterIndex + diffFromHandle;
    if (patternIndexToUse >= 0 && patternIndexToUse < numPatternBars) return peakHighlightPattern[patternIndexToUse];
    return 0.03; 
  });
  return (
    <div className="flex items-center justify-between h-6 w-full relative pointer-events-none">
      {displayedOpacities.map((opacity, index) => (
        <div 
          key={index} 
          className="h-full rounded-full" 
          style={{ 
            width: '2px', 
            backgroundColor: `rgba(250, 247, 245, ${opacity})`
          }} 
        />
      ))}
    </div>
  );
};

// --- Custom Slider Component ---
const CustomSlider = ({ 
  label, value, onChange, enableDragTooltip = false, tooltipText = "", 
  isTooltipVisible, onSliderHover, onSliderLeave, onSliderInteraction 
}) => {
  const trackWrapperRef = useRef(null); 
  const handleRef = useRef(null); 
  const inputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [inputValue, setInputValue] = useState(value.toString());

  useEffect(() => { setInputValue(value.toString()); }, [value]);

  const calculateValueFromPosition = useCallback((clientX) => {
    if (!trackWrapperRef.current) return value; 
    const trackRect = trackWrapperRef.current.getBoundingClientRect(); 
    const handleWidth = handleRef.current ? handleRef.current.offsetWidth : 10; 
    let positionInTrack = clientX - trackRect.left;
    let handleCenterPosition = positionInTrack - (handleWidth / 2);
    let newValue = (handleCenterPosition / (trackWrapperRef.current.offsetWidth - handleWidth)) * 100; 
    return Math.max(0, Math.min(100, Math.round(newValue)));
  }, [value]);

  const handleInteractionStart = useCallback((event) => {
    event.preventDefault(); 
    setIsDragging(true);
    if (onSliderInteraction) onSliderInteraction(); 
  }, [onSliderInteraction]);
  
  const handleInteractionMove = useCallback((clientX) => {
    if (!isDragging || !trackWrapperRef.current) return;
    onChange(calculateValueFromPosition(clientX)); 
  }, [isDragging, calculateValueFromPosition, onChange]);

  const handleInteractionEnd = useCallback(() => { setIsDragging(false); }, []);

  const handleDoubleClick = useCallback(() => {
    onChange(50); 
    if (isDragging) setIsDragging(false);
    if (onSliderInteraction) onSliderInteraction(); 
  }, [onChange, isDragging, onSliderInteraction]);

  const handleSliderComponentHover = () => {
    if (onSliderHover) onSliderHover();
  };

  const handleSliderComponentLeave = () => {
    if (onSliderLeave) onSliderLeave();
  };
  
  useEffect(() => {
    const onMouseMove = (event) => handleInteractionMove(event.clientX);
    const onMouseUp = () => handleInteractionEnd();
    if (isDragging) {
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [isDragging, handleInteractionMove, handleInteractionEnd]);

  useEffect(() => {
    const onTouchMove = (event) => { 
      if (event.touches.length > 0) handleInteractionMove(event.touches[0].clientX); 
    };
    const onTouchEnd = () => handleInteractionEnd();
    if (isDragging) {
      window.addEventListener('touchmove', onTouchMove, { passive: false });
      window.addEventListener('touchend', onTouchEnd);
    }
    return () => {
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
    };
  }, [isDragging, handleInteractionMove, handleInteractionEnd]);
  
  const handleLeftPosition = `calc(${value}% - 5px)`; 
  const handleInputChange = (e) => setInputValue(e.target.value);
  const applyInputValue = () => {
    let numericValue = parseInt(inputValue, 10);
    if (!isNaN(numericValue)) {
      numericValue = Math.max(0, Math.min(100, numericValue));
      onChange(numericValue); 
      setInputValue(numericValue.toString()); 
    } else { 
      setInputValue(value.toString()); 
    }
  };
  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter') { 
      applyInputValue(); 
      e.target.blur(); 
    } 
    else if (e.key === 'Escape') { 
      setInputValue(value.toString()); 
      e.target.blur(); 
    }
  };
  const handleInputFocus = () => { 
    if (onSliderInteraction) onSliderInteraction(); 
  };

  return (
    <div 
      className="border border-white/10 rounded-lg px-4 py-2 h-[48px] flex justify-between items-center space-x-3 relative cursor-default" 
      style={{ backgroundColor: colors.bg.primary }}
      onMouseEnter={handleSliderComponentHover}
      onMouseLeave={handleSliderComponentLeave}
    >
      <div className="flex items-center space-x-1 w-[110px] shrink-0 pointer-events-none">
        <span className="text-xs font-medium truncate" style={{ color: colors.text.primary }}>
          {label}
        </span>
        <div className="w-4 h-4 flex items-center justify-center shrink-0" style={{ color: colors.text.inactive }}>
          <InfoIcon />
        </div>
      </div>
      <div 
        ref={trackWrapperRef} 
        className="relative flex-1 h-6 flex items-center justify-center cursor-pointer select-none min-w-[84px]" 
        onMouseDown={(e) => { 
          handleInteractionStart(e); 
          handleInteractionMove(e.clientX); 
        }} 
        onTouchStart={(e) => { 
          handleInteractionStart(e); 
          if (e.touches.length > 0) handleInteractionMove(e.touches[0].clientX); 
        }} 
        onDoubleClick={handleDoubleClick}
      >
        <SliderTrack currentValue={value} />
        <div 
          ref={handleRef} 
          className="absolute h-full w-[10px] rounded-full shadow-[0_4px_12px_rgba(0,0,0,0.4)] cursor-grab active:cursor-grabbing z-10" 
          style={{ 
            left: handleLeftPosition, 
            top: '0px',
            backgroundColor: colors.accent.brand
          }} 
          onMouseDown={handleInteractionStart} 
          onTouchStart={handleInteractionStart} 
        />
        {enableDragTooltip && <DragTooltip value={value} isVisible={isDragging} parentHandleRef={handleRef} />}
      </div>
      <div className="w-[40px] shrink-0 flex items-center justify-end">
        <input 
          ref={inputRef} 
          type="text" 
          value={inputValue} 
          onClick={(e) => e.stopPropagation()} 
          onFocus={handleInputFocus} 
          onChange={handleInputChange} 
          onBlur={applyInputValue} 
          onKeyDown={handleInputKeyDown} 
          className="bg-transparent border-none text-xs font-medium text-right w-[28px] focus:outline-none focus:ring-1 focus:ring-gray-700 focus:rounded-sm p-0 m-0" 
          style={{ 
            fontFamily: 'PP Neue Montreal',
            fontWeight: 500,
            color: colors.text.primary,
            caretColor: colors.accent
          }} 
          maxLength={3} 
        />
        <span className="text-xs font-medium pointer-events-none" style={{ color: colors.text.primary }}>
          %
        </span>
      </div>
      {tooltipText && <InfoTooltip text={tooltipText} isVisible={isTooltipVisible} />}
    </div>
  );
};

// --- Toggle Component ---
const ToggleOption = ({ 
  label, value, onChange, tooltipText = "", 
  isTooltipVisible, onToggleHover, onToggleLeave, onToggleInteraction 
}) => {
  const handleToggleAreaClick = () => {
    onChange(!value);
    if (onToggleInteraction) onToggleInteraction();
  };

  const handleComponentHover = () => {
    if (onToggleHover) onToggleHover();
  };

  const handleComponentLeave = () => {
    if (onToggleLeave) onToggleLeave();
  };

  return (
    <div 
      className="border border-white/10 rounded-lg px-4 py-3 h-[48px] flex justify-between items-center relative cursor-pointer" 
      style={{ backgroundColor: colors.bg.primary }}
      onMouseEnter={handleComponentHover}
      onMouseLeave={handleComponentLeave}
    >
      <div className="flex items-center space-x-1 w-[110px] shrink-0">
        <span className="text-xs font-medium" style={{ color: colors.text.primary }}>
          {label}
        </span>
        <div className="w-4 h-4 flex items-center justify-center shrink-0" style={{ color: colors.text.inactive }}>
          <InfoIcon />
        </div>
      </div>
      
      <div className="flex items-center space-x-4">
        <div className="flex items-center space-x-4 cursor-pointer toggle-area" onClick={handleToggleAreaClick}>
          <span 
            className={`text-xs font-medium ${!value ? '' : 'opacity-30'}`} 
            style={{ color: colors.text.primary }}
          >
            Off
          </span>
          <span 
            className={`text-xs font-medium ${value ? '' : 'opacity-30'}`} 
            style={{ color: colors.text.primary }}
          >
            On
          </span>
        </div>
      </div>
      
      {tooltipText && <InfoTooltip text={tooltipText} isVisible={isTooltipVisible} />}
    </div>
  );
};

// --- Main Advanced Options Component ---
const AdvancedOptions = ({ hasAudio = false, onMumbleModeChange, onVocalGenderChange }) => {
  const [weirdness, setWeirdness] = useState(50);
  const [styleInfluence, setStyleInfluence] = useState(50);
  const [audioInfluence, setAudioInfluence] = useState(50);
  const [mumbleMode, setMumbleMode] = useState(false); // Default off
  const [vocalGender, setVocalGender] = useState(false); // Default off
  const [isExpanded, setIsExpanded] = useState(false);
  const [excludeStylesText, setExcludeStylesText] = useState(""); 

  const [activeTooltipId, setActiveTooltipId] = useState(null);
  const tooltipTimeoutRef = useRef(null);
  const hoverTimeoutRef = useRef(null); 

  const EXCLUDE_STYLES_TOOLTIP_ID = 'excludeStyles';
  const EXCLUDE_STYLES_TOOLTIP_TEXT = "List styles or keywords to avoid";

  const clearExistingTooltipTimeout = useCallback(() => {
    if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
        tooltipTimeoutRef.current = null;
    }
  },[]);

  const clearHoverTimeout = useCallback(() => {
    if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
    }
  },[]);

  const showTooltip = useCallback((tooltipId, duration) => {
    clearExistingTooltipTimeout();
    setActiveTooltipId(tooltipId);
    tooltipTimeoutRef.current = setTimeout(() => {
        setActiveTooltipId(null);
        tooltipTimeoutRef.current = null;
    }, duration);
  }, [clearExistingTooltipTimeout]);

  const showTooltipWithDelay = useCallback((tooltipId, duration) => {
    clearHoverTimeout();
    hoverTimeoutRef.current = setTimeout(() => {
      showTooltip(tooltipId, duration);
    }, 300); // 0.3s delay
  }, [showTooltip, clearHoverTimeout]);

  const hideTooltip = useCallback(() => {
    clearHoverTimeout();
    clearExistingTooltipTimeout();
    setActiveTooltipId(null);
  }, [clearHoverTimeout, clearExistingTooltipTimeout]);

  
  useEffect(() => {
    return () => {
      clearExistingTooltipTimeout();
      clearHoverTimeout();
    };
  }, [clearExistingTooltipTimeout, clearHoverTimeout]);

  const toggleExpansion = () => {
    setIsExpanded(!isExpanded);
    hideTooltip(); 
  }

  const handleExcludeStylesInputChange = (e) => {
    setExcludeStylesText(e.target.value);
    hideTooltip();
  };

  const handleMumbleModeChange = (newValue) => {
    setMumbleMode(newValue);
    if (onMumbleModeChange) {
      onMumbleModeChange(newValue);
    }
  };

  const handleVocalGenderChange = (newValue) => {
    setVocalGender(newValue);
    if (onVocalGenderChange) {
      onVocalGenderChange(newValue);
    }
  };

  const sliderConfigs = [
    { id: 'weirdness', label: "Weirdness", value: weirdness, setter: setWeirdness, tooltip: "Turn it up for wild, unexpected results" },
    { id: 'styleInfluence', label: "Style Influence", value: styleInfluence, setter: setStyleInfluence, tooltip: "How closely it follows your style description" },
    ...(hasAudio ? [{ id: 'audioInfluence', label: "Audio Influence", value: audioInfluence, setter: setAudioInfluence, tooltip: "How closely it resembles your audio" }] : []),
  ];

  return (
    <>
      <style>{`
        .exclude-styles-input::placeholder {
          color: ${colors.text.inactive};
        }
      `}</style>
      <div 
        className="rounded-xl shadow-lg p-0 min-h-0 w-full"
        style={{ backgroundColor: colors.bg.fog.thin }}
      >
      <div 
        className={`flex justify-between items-center px-4 min-h-[72px] ${!isExpanded ? 'cursor-pointer' : ''}`}
        onClick={!isExpanded ? toggleExpansion : undefined}
      >
        <div className={`flex items-center gap-2 ${isExpanded ? 'cursor-pointer' : ''}`} onClick={isExpanded ? toggleExpansion : undefined}>
          <div 
            className="flex items-center justify-center w-4 h-4 transition-transform duration-200"
            style={{ transform: !isExpanded ? 'rotate(-90deg)' : 'rotate(0deg)' }}
          >
            <ChevronDownIcon />
          </div>
          <h2 style={{ ...typography.small.title, color: colors.text.primary }}>
            Advanced options
          </h2>
        </div>
      </div>

      {isExpanded && (
        <div className="px-3 pb-3 pt-0 space-y-2">
          {/* Exclude Styles Input */}
          <div 
            className="border border-white/10 rounded-lg px-4 py-2 h-[48px] flex items-center space-x-2 relative cursor-default"
            style={{ backgroundColor: colors.bg.primary }}
            onMouseEnter={() => showTooltipWithDelay(EXCLUDE_STYLES_TOOLTIP_ID, 2500)}
            onMouseLeave={hideTooltip}
          >
            <div className="w-4 h-4 flex items-center justify-center shrink-0">
              <MusicNoteSlashIcon style={{ color: colors.text.primary }} />
            </div> 
            <input
              type="text"
              placeholder="Exclude styles"
              value={excludeStylesText}
              onClick={(e) => e.stopPropagation()}
              onChange={handleExcludeStylesInputChange}
              className="exclude-styles-input flex-grow bg-transparent border-none text-xs font-medium focus:outline-none p-1" 
              style={{
                fontFamily: 'PP Neue Montreal',
                fontWeight: 500,
                color: colors.text.primary,
                caretColor: colors.accent
              }}
            />
            {EXCLUDE_STYLES_TOOLTIP_TEXT && (
              <InfoTooltip 
                text={EXCLUDE_STYLES_TOOLTIP_TEXT} 
                isVisible={activeTooltipId === EXCLUDE_STYLES_TOOLTIP_ID} 
              />
            )}
          </div>

          {/* Sliders */}
          {sliderConfigs.map(slider => (
            <CustomSlider 
              key={slider.id}
              label={slider.label} 
              value={slider.value} 
              onChange={slider.setter} 
              enableDragTooltip={true} 
              tooltipText={slider.tooltip}
              isTooltipVisible={activeTooltipId === slider.label}
              onSliderHover={() => showTooltipWithDelay(slider.label, 3000)}
              onSliderLeave={hideTooltip}
              onSliderInteraction={hideTooltip}
            />
          ))}

          {/* Mumble Mode Toggle */}
          <ToggleOption 
            label="Mumble mode"
            value={mumbleMode}
            onChange={handleMumbleModeChange}
            tooltipText="Sings gibberish over your song (disables lyrics)"
            isTooltipVisible={activeTooltipId === 'Mumble mode'}
            onToggleHover={() => showTooltipWithDelay('Mumble mode', 3000)}
            onToggleLeave={hideTooltip}
            onToggleInteraction={hideTooltip}
          />

          {/* Vocal Gender Toggle */}
          <ToggleOption 
            label="Vocal gender"
            value={vocalGender}
            onChange={handleVocalGenderChange}
            tooltipText="Change the gender of your vocals"
            isTooltipVisible={activeTooltipId === 'Vocal gender'}
            onToggleHover={() => showTooltipWithDelay('Vocal gender', 3000)}
            onToggleLeave={hideTooltip}
            onToggleInteraction={hideTooltip}
          />
        </div>
      )}
      </div>
    </>
  );
};

export default AdvancedOptions;